import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { Pencil, Trash2, PlusCircle } from "lucide-react";
import FilterBy from "../utils/FilterBy";
import "./GoalSettings.css";
import Loader from "components/Loaders/Loader";
import { FaWalking, FaRoad, FaFire } from "react-icons/fa";
import FilterLoader from "components/Loaders/OverlaySpinner"
import { UserCircle, Building2 } from "lucide-react";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";
const API_URL = process.env.REACT_APP_API_URL;

interface Goal {
  goal_uid: string;
  goal_name: string;
  description: string;
  goal_metric_type: string;
  goal_metric_value: string;
  from_datetime: string;
  to_datetime: string;
  goal_category: string;
  goal_status: string;
  goal_type: string;
  goal_source: string; // "org" or "self"
}

const timeZone = moment.tz.guess();


const convertUTCToLocal = (utcString: string) => {
  return moment.utc(utcString).tz(timeZone).format("YYYY-MM-DDTHH:mm");
};


export const toUtcDateTime = (time: string) => {
  if (!time) return "";
  return moment.tz(time, "YYYY-MM-DDTHH:mm", timeZone).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
};
// Function to render an icon based on goal metric type using react-icons
const renderGoalIcon = (metricType: string) => {
  if (metricType === "steps") {
    return <FaWalking className="goal-icon" />;
  } else if (metricType === "distance") {
    return <FaRoad className="goal-icon" />;
  } else if (metricType === "caloriesburned") {
    return <FaFire className="goal-icon" />;
  } else {
    return null;
  }
};
const GoalSettings = () => {
  useCheckSessionHeartBeat();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [filter, setFilter] = useState("active");
  const [goalCategory, setGoalCategory] = useState("all");
  const [goalPeriod, setGoalPeriod] = useState("all");
  const [goalMetricType, setGoalMetricType] = useState("all");
  const [allGoals, setAllGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);

  // ✅ Load last selected source OR default to "org"
  const savedSource = localStorage.getItem("selected_goal_source") || "org";
  const [selectedGoalSource, setSelectedGoalSource] = useState(savedSource);

  const [toggleMessage, setToggleMessage] = useState(
    savedSource === "org" ? "Showing Organization Goals" : "Showing Self Goals"
  );

  const navigate = useNavigate();
  const sessionUid = localStorage.getItem("session_uid") ?? "";
  const userUid = localStorage.getItem("user_uid") ?? "";
  const Privilege = localStorage.getItem("privilege") || "";
  const [filterLoading, setFilterLoading] = useState(false);

  const fetchGoals = async (category: string = "all") => {
    try {
      setLoading(true);

      const response = await fetch(
        `${API_URL}/api/goals?category=${category}`,  
        {
          headers: {
            "user-uid": userUid,
            "accept": "application/json",
          },credentials: "include",
        }
      );

      const data = await response.json();
      if (response.ok && Array.isArray(data.data)) {
        setAllGoals(data.data);
        setGoals(data.data); // default
      } else {
        setAllGoals([]);
        setGoals([]);
        console.error("Failed to fetch goals", data.message);
      }
    } catch (error) {
      console.error("Error fetching goals:", error);
      setAllGoals([]);
      setGoals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  useEffect(() => {
    setFilterLoading(true);

    const activeGoals = allGoals.filter((goal) => goal.goal_status === "active");

    const filteredGoalsMap = new Map();
    activeGoals.forEach((goal) => {
      const key = `${goal.goal_metric_type}-${goal.goal_source}`;
      const existing = filteredGoalsMap.get(key);

      if (!existing || new Date(goal.from_datetime) < new Date(existing.from_datetime)) {
        filteredGoalsMap.set(key, goal);
      }
    });

    const deduplicatedGoals = Array.from(filteredGoalsMap.values());

    const finalGoals = deduplicatedGoals.filter((goal) => {
      const categoryFilter = goalCategory === "all" || goal.goal_category === goalCategory;
      const periodFilter = goalPeriod === "all" || goal.goal_type === goalPeriod;
      const metricFilter = goalMetricType === "all" || goal.goal_metric_type === goalMetricType;
      const sourceFilter = goal.goal_source === selectedGoalSource;

      return categoryFilter && periodFilter && metricFilter && sourceFilter;
    });

    setGoals(finalGoals);
    setFilterLoading(false); // immediately stop loader
  }, [goalCategory, goalPeriod, goalMetricType, selectedGoalSource, allGoals]);


  // ✅ Remember user selection across page navigation
  const toggleGoalSource = () => {
    const newSource = selectedGoalSource === "org" ? "self" : "org";
    setSelectedGoalSource(newSource);

    // ✅ Save to localStorage
    localStorage.setItem("selected_goal_source", newSource);

    setToggleMessage(newSource === "org" ? "Showing Organization Goals" : "Showing Self Goals");

    // Clear the message after 2 seconds
    setTimeout(() => {
      setToggleMessage("");
    }, 2000);
  };

  const handleEditGoal = (goal: Goal) => {
    const formattedGoal = {
      ...goal,
      from_datetime: convertUTCToLocal(goal.from_datetime),
      to_datetime: convertUTCToLocal(goal.to_datetime),
    };

    navigate(`/edit-goal/${goal.goal_uid}`, { state: { goal: formattedGoal } });
  };

  const handleDeleteGoal = async (goal_uid: string) => {
    try {
      const response = await fetch(`${API_URL}/api/delete-goal`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "user-uid": userUid,
        },credentials: "include",
        body: JSON.stringify(goal_uid),
      });

      if (response.ok) {
        setGoals(goals.filter((goal) => goal.goal_uid !== goal_uid));
        setAllGoals(allGoals.filter((goal) => goal.goal_uid !== goal_uid));
        console.log("Goal deleted successfully");
      } else {
        console.error("Failed to delete goal");
      }
    } catch (error) {
      console.error("Error deleting goal:", error);
    }
  };

  return (
    <div className="goal-settings-container">
      {filterLoading && <FilterLoader />}
      <>
        <div className="goal-settings-header">
          {/* <FilterBy
            options={["all", "fitness"]}
            onFilterChange={(val) => {
              setFilterLoading(true);
              setGoalCategory(val);
              fetchGoals(val);
            }}
            heading="Category"
          /> */}
          <select
            value={goalMetricType}
            onChange={(e) => {
              setFilterLoading(true);
              setGoalMetricType(e.target.value);
            }}
          >
            <option value="all">All Types</option>
            <option value="steps">Steps</option>
            <option value="distance">Distance</option>
            <option value="caloriesburned">caloriesburned</option>
          </select>

          <button className="goal-settings-button" onClick={toggleGoalSource}>
            {selectedGoalSource === "org" ? "Switch to Self Goals" : "Switch to Org Goals"}
          </button>

        </div>

        {toggleMessage && (
          <div className="goal-settings-toggle-message">
            {toggleMessage}
          </div>
        )}
        <div className="GoalSettingsHeadnIcon">
          {selectedGoalSource === "self" ? (
            <>
              <UserCircle size={20} />
              <span>Self Goals</span>
            </>
          ) : (
            <>
              <Building2 size={20} />
              <span>Organization Goals</span>
            </>
          )}
        </div>

        <div className="goal-list">
          {goals.length > 0 ? (
            goals.map((goal) => (
              <div key={goal.goal_uid} className="goal-card">
                <div className="goal-icon-container">
                  {renderGoalIcon(goal.goal_metric_type)}
                </div>

                <div className="goal-content">
                  <h3>{goal.goal_name}</h3>
                  <p>{goal.description}</p>
                  <p>
                    Type: {goal.goal_metric_type} - {goal.goal_metric_value}
                  </p>
                  <p>Period: {goal.goal_type}</p>
                </div>

                <div className="goal-actions">
                  {(selectedGoalSource === "self" || Privilege === "orgadmin" || Privilege === "systemadmin") && (
                    <button
                      className="goal-settings-edit-btn"
                      onClick={() => handleEditGoal(goal)}
                    >
                      <Pencil size={20} />
                    </button>
                  )}
                </div>

              </div>
            ))
          ) : (
            <p className="no-goals">No goals available, add one!</p>
          )}
        </div>
      </>
    </div>
  );
};

export default GoalSettings;